import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import {
  POWERBI_MAP,
  type PowerBiReportRoute,
} from 'walden-fast/constants/power-bi';
import type FeatureFlagsService from 'walden-fast/services/feature-flags';
import KpiService from 'walden-fast/services/kpi';
import type PowerBiService from 'walden-fast/services/power-bi';
import type UserService from 'walden-fast/services/user';
import type { PowerBiReportInternalCode } from 'walden-fast/types/fast-backend/power-bi';
import { getShowcaseContent } from 'walden-fast/constants/view-teasers';
import type { IntlService } from 'ember-intl';
import {
  SERVICE_NAMES,
  type ServiceName,
} from 'walden-fast/utils/constants/services';

type Resolved<P> = P extends Promise<infer T> ? T : P;
export type AppWaldenViewModel = Resolved<
  ReturnType<AppWaldenViewRoute['model']>
>;

type AppWaldenViewRouteParams = { report: PowerBiReportRoute };

const INDEX_ROUTE_SERVICE_NAME_MAPPING: Record<
  PowerBiReportRoute,
  ServiceName
> = {
  'inbound-outbound': SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
  inventory: SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
  'order-details': SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
  'master-data': SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
  transport: SERVICE_NAMES.WALDEN_VIEW_TRANSPORT,
  'order-tracking': SERVICE_NAMES.WALDEN_VIEW_ORDER_TRACKING,
  'order-to-cash': SERVICE_NAMES.WALDEN_VIEW_OTC,
  'walden-ml': SERVICE_NAMES.WALDEN_ML,
  'walden-orchestration': SERVICE_NAMES.WALDEN_ORCHESTRATION,
};

const SERVICE_NAME_MAPPING: Record<PowerBiReportInternalCode, ServiceName> = {
  Warehousing: SERVICE_NAMES.WALDEN_VIEW_WAREHOUSING,
  Order2Cash: SERVICE_NAMES.WALDEN_VIEW_OTC,
  Transport: SERVICE_NAMES.WALDEN_VIEW_TRANSPORT,
  OrderTracking: SERVICE_NAMES.WALDEN_VIEW_ORDER_TRACKING,
  WaldenML: SERVICE_NAMES.WALDEN_ML,
  Orchestration: SERVICE_NAMES.WALDEN_ORCHESTRATION,
  KPI1: SERVICE_NAMES.WALDEN_DASHBOARD,
  KPI2: SERVICE_NAMES.WALDEN_DASHBOARD,
  KPI3: SERVICE_NAMES.WALDEN_DASHBOARD,
  KPI4: SERVICE_NAMES.WALDEN_DASHBOARD,
  KPI5: SERVICE_NAMES.WALDEN_DASHBOARD,
  KPI6: SERVICE_NAMES.WALDEN_DASHBOARD,
  WaldenGreen: SERVICE_NAMES.WALDEN_GREEN_LEVEL1,
};

export default class AppWaldenViewRoute extends Route {
  @service('feature-flags') declare featureFlagsService: FeatureFlagsService;
  @service('kpi') declare kpiService: KpiService;
  @service('power-bi') declare powerBiService: PowerBiService;
  @service('router') declare routerService: RouterService;
  @service('user') declare userService: UserService;
  @service('intl') declare intlService: IntlService;

  async beforeModel(transition: Transition): Promise<Transition> {
    if (!transition.to) return;

    const params = transition.to?.parent?.params as AppWaldenViewRouteParams;

    const featureFlag = POWERBI_MAP[params.report]?.featureFlag;
    if (featureFlag) {
      if (!(await this.featureFlagsService.isEnabled(featureFlag))) {
        transition.abort();
        return this.routerService.transitionTo('/not-found', '');
      }
    } else {
      return this.routerService.transitionTo('/not-found', '');
    }
  }

  async model(params: AppWaldenViewRouteParams) {
    try {
      const userCanAccessService = this.userService.canAccessService(
        INDEX_ROUTE_SERVICE_NAME_MAPPING[params.report],
      );
      const result = userCanAccessService
        ? await this.powerBiService.getTokenReportAndPages({
            reportRoute: params.report,
          })
        : null;

      if (!result) {
        console.warn('Report not found');
        return {
          showcaseContent: this.showCaseContentMapping[params.report],
          userCanAccessService,
        };
      }

      const { pages, report, token } = result;

      return {
        pages,
        report,
        showExternalLinks: await this.featureFlagsService.isEnabled(
          'WALDEN_SHOW_EXTERNAL_POWERBI_LINKS',
        ),
        stats: userCanAccessService
          ? await this.kpiService.getStatsForPage(params.report)
          : [],
        token,
        userCanAccessService: this.userService.canAccessService(
          SERVICE_NAME_MAPPING[report.internalCode],
        ),
        showcaseContent: this.showCaseContentMapping[params.report],
      };
    } catch (err) {
      console.warn(err);

      return {
        showcaseContent: this.showCaseContentMapping[params.report],
        userCanAccessService: false,
      };
    }
  }

  get showCaseContentMapping() {
    return getShowcaseContent(this.intlService);
  }
}
